import dayjs, { Dayjs } from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(dayOfYear);
dayjs.extend(weekday);
dayjs.extend(utc);
dayjs.extend(timezone);
import Hours from '../models/Hours';
import SpecialHours from '../models/SpecialHours';
import { PreorderType } from '../enums/PreorderType';
import { Order } from '@adyen/adyen-web';
import Venue from '../models/Venue';

export class TimeUtils {
	static isBetween(dayJs: Dayjs, from: Dayjs, to: Dayjs): boolean {
		return dayJs.isAfter(from) && dayJs.isBefore(to);
	}
	static doesHoursMatch(mom: Dayjs, schedule: Hours[]): boolean {
		if (!schedule || schedule.length === 0) {
			return true;
		}
		let weekday = mom.weekday() - 1;
		if (weekday < 0) {
			weekday = 6;
		}
		for (const hour of schedule) {
			if (hour.weekday !== weekday) {
				continue;
			}
			const hours = TimeUtils.hoursToSchedule(hour);
			if (TimeUtils.isBetween(mom, hours.openedAt, hours.closedAt)) {
				return true;
			}
		}
		return false;
	}

	static doesHoursMatchWithCelebration(mom: Dayjs, schedule: Hours[], specialHours: SpecialHours[] = []): boolean {
		if (!schedule || schedule.length === 0) {
			return true;
		}
		let weekday = mom.weekday() - 1;
		if (weekday < 0) {
			weekday = 6;
		}
		if (specialHours.length > 0) {
			const foundSpecialDay = specialHours.find(specialDay => dayjs(specialDay?.date).isSame(dayjs(), 'day'));
			if (foundSpecialDay) {
				if (foundSpecialDay?.isClosed) {
					return false;
				}
				const specialHours = this.transformSpecialHours(foundSpecialDay);

				return TimeUtils.isBetween(mom, specialHours.openedAt, specialHours.closedAt);
			}
		}
		for (const hour of schedule) {
			if (hour.weekday !== weekday) {
				continue;
			}
			const hours = TimeUtils.hoursToSchedule(hour);

			console.log(hours);
			if (TimeUtils.isBetween(mom, hours.openedAt, hours.closedAt)) {
				return true;
			}
		}
		return false;
	}

	static transformSpecialHours(specialHours: SpecialHours) {
		const now = dayjs().second(0).millisecond(0);
		if (specialHours.isClosed) {
			return null;
		}
		const openedAtTokens = specialHours.openedAt.split(':').map(token => Number(token));
		const closedAtTokens = specialHours.closedAt.split(':').map(token => Number(token));
		let openedAt = this.tokensToDayjs(now, openedAtTokens, false);
		let closedAt = this.tokensToDayjs(now, closedAtTokens, false);
		if (closedAt.isBefore(openedAt)) {
			closedAt = closedAt.add(1, 'days');
		}
		const nowWeekday = now.weekday();
		const daysToAdd =
			dayjs(specialHours.date).weekday() < nowWeekday
				? 7 - (nowWeekday - dayjs(specialHours.date).weekday())
				: dayjs(specialHours.date).weekday() - nowWeekday;
		openedAt = openedAt.add(daysToAdd, 'days');
		closedAt = closedAt.add(daysToAdd, 'days');

		return {
			openedAt: openedAt,
			closedAt: closedAt,
		};
	}

	static doesHoursMatchNow(schedule: Hours[]): boolean {
		if (!schedule || schedule.length === 0) {
			return true;
		}
		// Hours are pre-sanitized to browser-local time at venue load; compare against browser-local now.
		const now = dayjs();
		let weekday = now.weekday() - 1;
		if (weekday < 0) {
			weekday = 6;
		}
		const prevWeekday = (weekday + 6) % 7;
		const nowMinutes = now.hour() * 60 + now.minute();

		for (const hour of schedule) {
			const [oh, om] = hour.openedAt.split(':').map(Number);
			const [ch, cm] = hour.closedAt.split(':').map(Number);
			const openMin = oh * 60 + om;
			const rawCloseMin = ch * 60 + cm;
			const crossesMidnight = rawCloseMin <= openMin;

			if (hour.weekday === weekday) {
				const closeMin = crossesMidnight ? rawCloseMin + 1440 : rawCloseMin;
				if (nowMinutes > openMin && nowMinutes < closeMin) {
					return true;
				}
			}

			// Slot that opened the previous day and crosses midnight into today
			if (hour.weekday === prevWeekday && crossesMidnight) {
				const adjustedNow = nowMinutes + 1440;
				const closeMin = rawCloseMin + 1440;
				if (adjustedNow > openMin && adjustedNow < closeMin) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * Create array of dates on this week
	 * @param hours Hours to sanitize
	 */
	static createArrayOfWeek(): Dayjs[] {
		var startOfWeek = dayjs().startOf('week').add(1, 'day');
		var endOfWeek = dayjs().endOf('week').add(1, 'day');

		var days = [];
		var day = startOfWeek;

		while (day <= endOfWeek) {
			days.push(day.toDate());
			day = day.clone().add(1, 'd');
		}

		return days;
	}

	/**
	 * Converts hours to LocalTime and puts Hours together if they are one after the other
	 * @param hours Hours to sanitize
	 */
	static sanitizeHours(hours: Hours[], specialHours: SpecialHours[] = []): Hours[] {
		if (!hours || hours.length === 0) {
			return [];
		}
		const result: Hours[] = [];
		const weekArrayDates = this.createArrayOfWeek();
		for (const day of weekArrayDates) {
			const hoursArrayFound = hours.filter(hour => {
				let weekday = dayjs(day).weekday() - 1;
				if (weekday < 0) {
					weekday = 6;
				}

				return weekday === hour.weekday;
			});
			hoursArrayFound.forEach(hour => {
				if (hour) {
					try {
						const dateExistIndex = weekArrayDates.findIndex(it => {
							let weekday = dayjs(it).weekday() - 1;
							if (weekday < 0) {
								weekday = 6;
							}
							return weekday === hour.weekday;
						});
						const specialDates = specialHours.filter(it => dayjs(it.date).isSame(dayjs(weekArrayDates[dateExistIndex]), 'day'));

						if (specialHours.length > 0 && dateExistIndex >= 0 && specialDates.length > 0) {
							specialDates.forEach(specialDate => {
								const specialHoursObject = this.transformSpecialHours(specialDate);
								let weekday = dayjs(specialDate.date).weekday() - 1;
								if (weekday < 0) {
									weekday = 6;
								}
								if (!specialDate.isClosed) {
									const localHour = new Hours();

									localHour.weekday = weekday;
									localHour.openedAt = specialHoursObject.openedAt.format('HH:mm');
									localHour.closedAt = specialHoursObject.closedAt.format('HH:mm');
									result.push(localHour);
								} else {
									if (specialDate.isClosed && specialDate.closedAt && specialDate.openedAt) {
										const schedule = TimeUtils.hoursToSchedule(hour, false);

										if (dayjs(specialHoursObject.openedAt).isAfter(schedule.openedAt)) {
											const localHour = new Hours();
											localHour.openedAt = schedule.openedAt.format('HH:mm');
											localHour.closedAt = specialHoursObject.openedAt.format('HH:mm');
											localHour.weekday = weekday;

											result.push(localHour);
										}
										if (dayjs(schedule.closedAt).isAfter(specialHoursObject.closedAt)) {
											const localHour = new Hours();
											localHour.openedAt = specialHoursObject.closedAt.format('HH:mm');
											localHour.closedAt = schedule.closedAt.format('HH:mm');
											localHour.weekday = weekday;

											result.push(localHour);
										}
										if (dayjs(specialHoursObject.closedAt).isAfter(schedule.closedAt)) {
											const localHour = new Hours();
											localHour.openedAt = schedule.closedAt.format('HH:mm');
											localHour.closedAt = specialHoursObject.closedAt.format('HH:mm');
											localHour.weekday = weekday;

											result.push(localHour);
										}
										// localHour.weekday = hour.weekday;
										// localHour.openedAt = schedule.openedAt.format('HH:mm');
										// localHour.closedAt = schedule.closedAt.format('HH:mm');
									}
								}
							});
						} else {
							const schedule = TimeUtils.hoursToSchedule(hour, false);
							const localHour = new Hours();

							localHour.weekday = hour.weekday;
							localHour.openedAt = schedule.openedAt.format('HH:mm');
							localHour.closedAt = schedule.closedAt.format('HH:mm');
							result.push(localHour);
							if (localHour.openedAt === '00:00') {
								const prevDay = localHour.weekday === 0 ? 6 : localHour.weekday - 1;
								const prev = result.find(prevHour => prevHour.weekday === prevDay && prevHour.closedAt === '23:59');
								if (prev) {
									prev.closedAt = localHour.closedAt;
									prev.connectedWithNext = true;
									localHour.connectedWithPrev = true;
								}
							}
						}
					} catch (e) {
						console.error(e);
					}
				}
			});
			if (hoursArrayFound.length === 0) {
				const specialDates = specialHours.filter(it => dayjs(it.date).isSame(dayjs(day), 'day'));
				if (specialDates && specialDates.length > 0) {
					specialDates.forEach(specialDate => {
						if (!specialDate?.isClosed) {
							const specialHoursObject = this.transformSpecialHours(specialDate);
							const localHour = new Hours();

							let weekday = dayjs(specialDate.date).weekday() - 1;
							if (weekday < 0) {
								weekday = 6;
							}
							localHour.weekday = weekday;
							localHour.openedAt = specialHoursObject.openedAt.format('HH:mm');
							localHour.closedAt = specialHoursObject.closedAt.format('HH:mm');
							result.push(localHour);
						}
					});
				}
			}
		}

		const first = result[0];
		if (first.openedAt === '00:00') {
			const prevDay = first.weekday === 0 ? 6 : first.weekday - 1;
			const prev = result.find(prevHour => prevHour.weekday === prevDay && prevHour.closedAt === '23:59');
			if (prev) {
				prev.closedAt = first.closedAt;
				prev.connectedWithNext = true;
				first.connectedWithPrev = true;
			}
		}
		return TimeUtils.sortHours(result);
	}

	static sortHours(hours: Hours[]): Hours[] {
		return hours.sort((a, b) => {
			if (a.weekday < b.weekday) {
				return -1;
			}
			if (a.weekday > b.weekday) {
				return 1;
			}
			if (a.weekday === b.weekday) {
				const openAtA = +a.openedAt.replace(':', '');
				const openAtB = +b.openedAt.replace(':', '');
				if (openAtA < openAtB) {
					return -1;
				}
				if (openAtA > openAtB) {
					return 1;
				}
				return 0;
			}
		});
	}

	static hoursToSchedule(
		hour: Hours,
		localTime: boolean = true,
		specialHours: SpecialHours[] = []
	): {
		openedAt: Dayjs;
		closedAt: Dayjs;
	} | null {
		if (!hour) {
			return null;
		}
		const now = dayjs().second(0).millisecond(0);
		const openedAtTokens = hour.openedAt.split(':').map(token => Number(token));
		const closedAtTokens = hour.closedAt.split(':').map(token => Number(token));
		let openedAt = this.tokensToDayjs(now, openedAtTokens, localTime);
		let closedAt = this.tokensToDayjs(now, closedAtTokens, localTime);
		if (hour.connectedWithNext || closedAt.isBefore(openedAt)) {
			closedAt = closedAt.add(1, 'days');
		}
		const nowWeekday = now.weekday() === 0 ? 6 : now.weekday() - 1;
		const daysToAdd = hour.weekday < nowWeekday ? 7 - (nowWeekday - hour.weekday) : hour.weekday - nowWeekday;
		openedAt = openedAt.add(daysToAdd, 'days');
		closedAt = closedAt.add(daysToAdd, 'days');
		if (specialHours.length > 0) {
			const foundSpecialDay = specialHours.find(it => dayjs(it.date).isSame(openedAt, 'day'));

			if (foundSpecialDay) {
				if (foundSpecialDay?.isClosed) {
					return null;
				}
				const transformSpecialDayToHours = this.transformSpecialHours(foundSpecialDay);
				return {
					openedAt: transformSpecialDayToHours.openedAt,
					closedAt: transformSpecialDayToHours.closedAt,
				};
			}
		}
		return {
			openedAt,
			closedAt,
		};
	}

	static tokensToDayjs(now: Dayjs, tokens: number[], localTime: boolean): Dayjs {
		let mom = dayjs();
		if (!localTime) {
			mom = mom.utc();
		}
		mom = mom.hour(tokens[0]).minute(tokens[1]).second(0).millisecond(0);
		if (!localTime) {
			mom = mom.local().date(now.date()).month(now.month()).year(now.year());
		}
		return mom;
	}

	static getSchedulesOfDay(mom: Dayjs, schedule: Hours[]): Hours[] {
		return schedule.filter(hour => {
			let weekday = mom.weekday() - 1;
			if (weekday < 0) {
				weekday = 6;
			}
			return weekday === hour.weekday;
		});
	}

	static isSameDateNew(m1: Dayjs, m2: Dayjs): boolean {
		if (m1 === m2) {
			return true;
		}
		if (!m1 || !m2) {
			return false;
		}
		const dayjs1 = dayjs(m1);
		const dayjs2 = dayjs(m2);
		return dayjs1.dayOfYear() === dayjs2.dayOfYear() && dayjs1.year() === dayjs2.year();
	}

	static isSameDate(m1: Dayjs, m2: Dayjs): boolean {
		return dayjs(m1)?.dayOfYear() === dayjs(m2)?.dayOfYear() && dayjs(m1)?.year() === dayjs(m2)?.year();
	}
}
